import styled, { css } from 'styled-components';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export const StyledButtonStyles = css`
  display: inline-block;
  text-decoration: none;
  border: none;
  outline: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  color: ${p => p.theme.colors.navFg};
  background-color: ${p => p.theme.colors.buttonPrimaryBg};
  padding: 1.2rem 1.8rem;
  border-radius: 20px;
  min-width: 140px;
  font-weight: bold;
  text-align: center;
  font-size: ${p => p.theme.fontSize.sm};

  :hover,
  :active,
  :focus {
    color: ${p => p.theme.colors.navFg};
    background-color: ${p => p.theme.colors.darkBlue};
  }

  :disabled {
    color: ${p => p.theme.colors.midGrey};
    background-color: ${p => p.theme.colors.lightGrey};
    :hover {
      box-shadow: none;
    }
  }
`;

const variantStyles = css<{ variant?: ButtonVariant }>`
  ${p => {
    switch (p.variant) {
      case 'secondary':
        return `
          color: ${p.theme.colors.deepBlue};
          background-color: transparent;
          border: 2px solid ${p.theme.colors.deepBlue};
          &:hover, &:active, &:focus {
            color: ${p.theme.colors.navFg};
            background-color: ${p.theme.colors.buttonPrimaryBg};
          }
        `;
      case 'ghost':
        return `
          color: ${p.theme.colors.deepBlue};
          background-color: transparent;
          border: none;
          &:hover, &:active, &:focus {
            color: ${p.theme.colors.darkBlue};
            background-color: transparent;
          }
        `;
      case 'danger':
        return `
          color: ${p.theme.colors.navFg};
          background-color: ${p.theme.colors.darkRed};
          border: none;
          &:hover, &:active, &:focus {
            color: ${p.theme.colors.navFg};
            background-color: ${p.theme.colors.deepRed};
          }
        `;
      default:
        return '';
    }
  }}
`;

const sizeStyles = css<{ size?: ButtonSize }>`
  ${p => {
    switch (p.size) {
      case 'sm':
        return `
          padding: ${p.theme.spacing.sm} ${p.theme.spacing.md};
          font-size: ${p.theme.fontSize.xs};
        `;
      case 'lg':
        return `
          padding: ${p.theme.spacing.base} ${p.theme.spacing.lg};
          font-size: ${p.theme.fontSize.base};
        `;
      default:
        return '';
    }
  }}
`;

export const Button = styled.button<{
  appendStyles?: any;
  variant?: ButtonVariant;
  size?: ButtonSize;
}>`
  ${StyledButtonStyles}
  ${variantStyles}
  ${sizeStyles}
  ${props => props.appendStyles}
`;

export const UnstyledButtonStyles = css`
  appearance: none;
  background-color: transparent;
  margin: 0;
  padding: 0;
  border: none;
  color: inherit;
  font-size: ${p => p.theme.fontSize.sm};
`;

export const UnstyledButton = styled.button<{ appendStyles?: any }>`
  ${UnstyledButtonStyles}
  ${props => props.appendStyles}
`;

export const KeyboardKeyButton = styled(UnstyledButton)<{
  isSelected?: boolean;
  hideButton?: boolean;
}>`
  outline: none;
  display: ${p => (p.hideButton ? 'none' : 'block')};
  color: ${p => p.theme.colors.actionFg};
  padding: 0;
  width: 44px;
  height: 41px;
  margin: 5px;
  background-color: ${p => p.theme.colors.lightGrey};
  border-radius: 4px;
  border-top: 1px solid ${p => p.theme.colors.borderSubtle};
  box-shadow:
    inset 0 0 25px #e8e8e8,
    0 1px 0 #c3c3c3,
    0 2px 0 #c9c9c9,
    0 2px 3px #333;

  :active {
    color: #888;
    background: #ccc;
    border-top: 1px solid #eee;
    box-shadow:
      inset 0 0 25px #ddd,
      0 0 3px #333;
  }

  ${props =>
    props.isSelected &&
    `
    color: #888;
    background: #ccc;
    border-top: 1px solid #eee;
    box-shadow: inset 0 0 25px #ddd, 0 0 3px #333;
  `};

  img {
    width: 30px;
    height: auto;
    position: relative;
    top: 2px;
  }
`;
